import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Sparkles, Clock, Target } from 'lucide-react';
import type { Profile } from '../types';

interface Props {
  patients: Profile[];
}

/**
 * Surfaces the "10x" proof-point metrics described in PITCH_NARRATIVE.md (Stage B):
 *  - Time-to-Plan: average time between an AI draft being generated and a
 *    mission from it being approved by the therapist.
 *  - Plan Adoption Rate: approved AI-suggested missions vs. total missions
 *    the AI suggested across all draft-generation events.
 */
const AIImpactMetrics: React.FC<Props> = ({ patients }) => {
  const [avgMinutes, setAvgMinutes] = useState<number | null>(null);
  const [adoptionRate, setAdoptionRate] = useState<number | null>(null);
  const [approvedCount, setApprovedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const patientIds = patients.map((p) => p.id);

      const [draftsResponse, missionsResponse] = await Promise.all([
        supabase
          .from('ai_session_drafts')
          .select('suggested_count')
          .eq('therapist_id', user.id),
        supabase
          .from('missions')
          .select('draft_generated_at, approved_at')
          .eq('ai_generated', true)
          .in('user_id', patientIds),
      ]);

      const drafts = draftsResponse.data || [];
      const missions = missionsResponse.data || [];

      const suggestedTotal = drafts.reduce((sum, d) => sum + (d.suggested_count || 0), 0);
      setApprovedCount(missions.length);
      setAdoptionRate(suggestedTotal > 0 ? (missions.length / suggestedTotal) * 100 : null);

      const durations = missions
        .filter((m) => m.draft_generated_at && m.approved_at)
        .map((m) => (new Date(m.approved_at!).getTime() - new Date(m.draft_generated_at!).getTime()) / 60000);

      setAvgMinutes(durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : null);
    } catch (err) {
      console.error('Failed to compute AI impact metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [patients]);

  useEffect(() => {
    if (patients.length > 0) {
      fetchMetrics();
    } else {
      setLoading(false);
    }
  }, [patients, fetchMetrics]);

  if (loading || (avgMinutes === null && adoptionRate === null)) return null;

  return (
    <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={18} className="text-primary" />
        <span className="text-sm font-bold text-foreground">השפעת ה-AI שלך</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card/50 rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
            <Clock size={14} />
            <span>זמן עד לתוכנית מאושרת</span>
          </div>
          <div className="text-lg font-bold text-primary">
            {avgMinutes !== null ? `${avgMinutes < 1 ? '<1' : Math.round(avgMinutes)} דק׳` : '—'}
          </div>
        </div>
        <div className="bg-card/50 rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
            <Target size={14} />
            <span>שיעור אימוץ הצעות AI</span>
          </div>
          <div className="text-lg font-bold text-accent">
            {adoptionRate !== null ? `${Math.round(adoptionRate)}% (${approvedCount})` : '—'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIImpactMetrics;
